import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Search,
  Filter,
  MoreHorizontal,
  Eye,
  Users,
  Mail,
  Calendar,
  DollarSign,
  TrendingUp,
  UserPlus,
  RefreshCw,
  AlertTriangle,
  Copy,
  CheckCircle,
  XCircle,
  Clock,
  ExternalLink,
  Settings,
  Download,
  Globe,
  Instagram,
  Facebook,
  Youtube,
  Twitter,
  Linkedin,
  Loader2,
  CreditCard,
  X,
  AlertCircle,
  Calculator,
  Bookmark,
  Camera,
  FileText,
  Music,
  BarChart3,
  Tag,
} from 'lucide-react';
import AffiliateSort from '../components/AffiliateSort';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
// Avatar component not available in admin portal - using div instead
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Pagination } from '../components/ui/pagination';
import { formatCurrency, formatDate, getInitials } from '../lib/utils';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  approveAffiliateApplication,
  rejectAffiliateApplication,
  getPayoutSchedule,
  updatePayoutSchedule,
  triggerAffiliatePayout,
  getAffiliateCommissions,
  getAffiliatePayouts,
  getAffiliateCommissionsPaginated,
  getAffiliatePayoutsPaginated,
  getAffiliateClicksInDateRange,
  getAffiliateConversionsInDateRange,
  PaginatedCommissionsResult,
  PaginatedPayoutsResult
} from '../services/affiliateService';
import { 
  getCommissionSettings, 
  updateCommissionSettings, 
  CommissionSettings 
} from '../services/commissionSettingsService';
import { AffiliateWithStats, PayoutSchedule, SocialProfile } from '../types';
import { useAffiliateStatistics, TimePeriod } from '../hooks/useAffiliateStatistics';
import { useCommissionSourceAnalytics } from '../hooks/useCommissionSourceAnalytics';
import { useDiscountCodePerformance } from '../hooks/useDiscountCodePerformance';
import { usePaginatedAffiliates } from '../hooks/usePaginatedAffiliates';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const Affiliates = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState<AffiliateWithStats | null>(null);
  const [isApproving, setIsApproving] = useState<string | null>(null);
  const [isRejecting, setIsRejecting] = useState<string | null>(null);
  const [payoutSchedule, setPayoutSchedule] = useState<PayoutSchedule | null>(null);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [affiliateToApprove, setAffiliateToApprove] = useState<AffiliateWithStats | null>(null);
  const [affiliateToReject, setAffiliateToReject] = useState<AffiliateWithStats | null>(null);
  const [referralCode, setReferralCode] = useState('');

  // Use the new paginated affiliates hook
  const {
    affiliates,
    loading,
    error,
    totalCount,
    currentPage,
    totalPages,
    searchQuery,
    selectedStatus,
    sortField,
    sortOrder,
    isSearchMode,
    isFilterMode,
    isSortMode,
    isCombinedMode,
    searchLoading,
    filterLoading,
    sortLoading,
    setSelectedStatus,
    setSorting,
    handleSearch,
    fetchPage,
    refresh,
    updateAffiliateInCache,
    removeAffiliateFromCache,
    invalidateCache,
  } = usePaginatedAffiliates({ pageSize: 20 });

  // Commission settings state
  const [commissionSettings, setCommissionSettings] = useState<CommissionSettings | null>(null);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showCommissionConfirmModal, setShowCommissionConfirmModal] = useState(false);
  const [newCommissionRate, setNewCommissionRate] = useState<string>('');
  const [loadingCommissionSettings, setLoadingCommissionSettings] = useState(false);

  // Payout settings state
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [showPayoutConfirmModal, setShowPayoutConfirmModal] = useState(false);
  const [newPayoutSettings, setNewPayoutSettings] = useState({
    frequency: 'monthly' as 'monthly' | 'quarterly',
    dayOfMonth: 1,
    time: '09:00'
  });
  const [loadingPayoutSettings, setLoadingPayoutSettings] = useState(false);

  // Time period state for statistics
  const [selectedTimePeriod, setSelectedTimePeriod] = useState<TimePeriod>('all_time');

  // Use the new affiliate statistics hook with time period
  const { 
    statistics, 
    percentageChanges,
    loading: statisticsLoading, 
    error: statisticsError,
    refresh: refreshStatistics 
  } = useAffiliateStatistics({ timePeriod: selectedTimePeriod });

  // Commission source analytics
  const {
    analytics: sourceAnalytics,
    loading: sourceAnalyticsLoading,
    error: sourceAnalyticsError,
    refresh: refreshSourceAnalytics
  } = useCommissionSourceAnalytics(selectedTimePeriod);

  // Discount code performance analytics
  const {
    performance: discountPerformance,
    loading: discountPerformanceLoading,
    error: discountPerformanceError,
    refresh: refreshDiscountPerformance
  } = useDiscountCodePerformance(selectedTimePeriod);

  // Affiliate statistics modal state
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [selectedAffiliateForStats, setSelectedAffiliateForStats] = useState<AffiliateWithStats | null>(null);
  const [statsDateFilter, setStatsDateFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [affiliateStats, setAffiliateStats] = useState({
    totalClicks: 0,
    totalConversions: 0,
    totalEarnings: 0,
    conversionRate: 0,
    avgOrderValue: 0
  });
  const [affiliateCommissions, setAffiliateCommissions] = useState<any[]>([]);
  const [affiliatePayouts, setAffiliatePayouts] = useState<any[]>([]);
  const [loadingAffiliateStats, setLoadingAffiliateStats] = useState(false);
  const [statsActiveFilter, setStatsActiveFilter] = useState<string | null>(null);
  
  // Commission source filtering
  const [commissionSourceFilter, setCommissionSourceFilter] = useState<'all' | 'link' | 'discount_code'>('all');
  
  // Chart colors for source analytics
  const CHART_COLORS = {
    link: '#3B82F6', // Blue
    discount: '#8B5CF6', // Purple
    background: ['#3B82F6', '#8B5CF6']
  };

  // Firebase pagination state for Commission Records
  const [commissionsCurrentPage, setCommissionsCurrentPage] = useState(1);
  const [commissionsTotalCount, setCommissionsTotalCount] = useState(0);
  const [commissionsPageSize] = useState(20);
  const [commissionsLastDoc, setCommissionsLastDoc] = useState<any>(null);
  const [commissionsHasMore, setCommissionsHasMore] = useState(false);
  const [commissionsPages, setCommissionsPages] = useState<Map<number, any>>(new Map());
  
  // Firebase pagination state for Payout History
  const [payoutsCurrentPage, setPayoutsCurrentPage] = useState(1);
  const [payoutsTotalCount, setPayoutsTotalCount] = useState(0);
  const [payoutsPageSize] = useState(20);
  const [payoutsLastDoc, setPayoutsLastDoc] = useState<any>(null);
  const [payoutsHasMore, setPayoutsHasMore] = useState(false);
  const [payoutsPages, setPayoutsPages] = useState<Map<number, any>>(new Map());

  // Payout preview modal state
  const [showPayoutPreviewModal, setShowPayoutPreviewModal] = useState(false);
  const [showPayoutConfirmation, setShowPayoutConfirmation] = useState(false);
  const [selectedAffiliateForPayout, setSelectedAffiliateForPayout] = useState<AffiliateWithStats | null>(null);
  const [previousMonthCommissions, setPreviousMonthCommissions] = useState<any[]>([]);
  const [payoutPreviewData, setPayoutPreviewData] = useState({
    totalAmount: 0,
    commissionCount: 0,
    hasExistingPayout: false,
    previousPayoutAmount: 0,
    previousPayoutDate: null as Date | null
  });
  const [loadingPayoutPreview, setLoadingPayoutPreview] = useState(false);

  // Previous month commission data for affiliate details
  const [affiliatePrevMonthData, setAffiliatePrevMonthData] = useState<{[key: string]: {amount: number, isPaid: boolean}}>({});

  // Handle URL search parameters from notifications
  useEffect(() => {
    const searchParam = searchParams.get('search');
    const statusParam = searchParams.get('status');
    
    if (searchParam && searchParam !== searchQuery) {
      // Auto-trigger search with the parameter value
      handleSearch(searchParam);
    }
    
    if (statusParam && statusParam !== selectedStatus) {
      // Auto-trigger status filter
      setSelectedStatus(statusParam);
    }
    
    // Clear URL parameters after applying them
    const newSearchParams = new URLSearchParams();
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, searchQuery, selectedStatus, handleSearch, setSelectedStatus, setSearchParams]);

  useEffect(() => {
    loadPayoutSchedule();
    loadCommissionSettings();
  }, []);

  const loadPayoutSchedule = async () => {
    try {
      const schedule = await getPayoutSchedule();
      setPayoutSchedule(schedule);
    } catch (error) {
      console.error('Error loading payout schedule:', error);
    }
  };

  const loadCommissionSettings = async () => {
    try {
      const settings = await getCommissionSettings();
      setCommissionSettings(settings);
    } catch (error) {
      console.error('Error loading commission settings:', error);
    }
  };

  const handleCommissionSettingsClick = () => {
    if (commissionSettings) {
      setNewCommissionRate(commissionSettings.commissionRate.toString());
    }
    setShowCommissionModal(true);
  };

  const handleCommissionSettingsUpdate = () => {
    const rate = parseFloat(newCommissionRate);
    
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error('Commission rate must be between 0 and 100');
      return;
    }

    // Show confirmation modal
    setShowCommissionConfirmModal(true);
  };

  const handleCommissionConfirmationUpdate = async () => {
    const rate = parseFloat(newCommissionRate);

    try {
      setLoadingCommissionSettings(true);
      await updateCommissionSettings(rate, 'admin');
      await loadCommissionSettings(); // Reload settings
      toast.success('Commission rate updated successfully');
      setShowCommissionModal(false);
      setShowCommissionConfirmModal(false);
      setNewCommissionRate(''); // Clear the input
    } catch (error) {
      console.error('Error updating commission settings:', error);
      toast.error('Failed to update commission rate');
    } finally {
      setLoadingCommissionSettings(false);
    }
  };

  // Payout settings handlers
  const handlePayoutSettingsClick = () => {
    if (payoutSchedule) {
      setNewPayoutSettings({
        frequency: payoutSchedule.frequency,
        dayOfMonth: payoutSchedule.dayOfMonth,
        time: payoutSchedule.time
      });
    }
    setShowPayoutModal(true);
  };

  const handlePayoutSettingsUpdate = () => {
    const { frequency, dayOfMonth, time } = newPayoutSettings;
    
    if (dayOfMonth < 1 || dayOfMonth > 31) {
      toast.error('Day of month must be between 1 and 31');
      return;
    }

    if (!time || !time.match(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)) {
      toast.error('Please enter a valid time in HH:MM format');
      return;
    }

    // Show confirmation modal
    setShowPayoutConfirmModal(true);
  };

  const handlePayoutConfirmationUpdate = async () => {
    const { frequency, dayOfMonth, time } = newPayoutSettings;

    try {
      setLoadingPayoutSettings(true);
      
      // Calculate next payout date based on frequency and day
      const nextPayoutDate = calculateNextPayoutDate(frequency, dayOfMonth);
      
      const updatedSchedule: PayoutSchedule = {
        id: payoutSchedule?.id || 'config',
        frequency,
        dayOfMonth,
        time,
        nextPayoutDate,
        commissionPercentage: commissionSettings?.commissionRate || 5
      };

      await updatePayoutSchedule(updatedSchedule);
      await loadPayoutSchedule(); // Reload schedule
      toast.success('Payout schedule updated successfully');
      setShowPayoutModal(false);
      setShowPayoutConfirmModal(false);
    } catch (error) {
      console.error('Error updating payout schedule:', error);
      toast.error('Failed to update payout schedule');
    } finally {
      setLoadingPayoutSettings(false);
    }
  };

  const calculateNextPayoutDate = (frequency: 'monthly' | 'quarterly', dayOfMonth: number): Date => {
    const now = new Date();
    const nextDate = new Date(now);
    
    if (frequency === 'monthly') {
      // Set to the specified day of the current month
      nextDate.setDate(dayOfMonth);
      
      // If the day has already passed this month, move to next month
      if (nextDate <= now) {
        nextDate.setMonth(nextDate.getMonth() + 1);
      }
    } else if (frequency === 'quarterly') {
      // Find the next quarter
      const currentMonth = now.getMonth();
      const quarterMonths = [0, 3, 6, 9]; // January, April, July, October
      
      let nextQuarterMonth = quarterMonths.find(month => month > currentMonth);
      if (!nextQuarterMonth) {
        nextQuarterMonth = quarterMonths[0]; // January of next year
        nextDate.setFullYear(nextDate.getFullYear() + 1);
      }
      
      nextDate.setMonth(nextQuarterMonth);
      nextDate.setDate(dayOfMonth);
    }
    
    return nextDate;
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    fetchPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Comprehensive refresh function that matches other pages
  const handleRefresh = async () => {
    try {
      await Promise.all([
        refresh(),
        refreshStatistics(),
        loadPayoutSchedule(),
        loadCommissionSettings()
      ]);
      toast.success('Data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh data');
    }
  };

  // Helper function to refresh affiliates after updates while maintaining current mode
  const refreshAffiliatesAfterUpdate = async () => {
    if (isSearchMode || isFilterMode || isSortMode || isCombinedMode) {
      // Invalidate cache and re-trigger current search/filter/sort to ensure updated affiliates are displayed correctly
      invalidateCache();
      
      // Re-trigger search if active
      if (searchQuery) {
        handleSearch(searchQuery);
      }
      
      // Re-trigger filters if active
      if (isFilterMode || isCombinedMode) {
        setSelectedStatus(selectedStatus);
      }
      
      // Re-trigger sorting if active
      if (isSortMode || isCombinedMode) {
        setSorting(sortField, sortOrder);
      }
      
      await refreshStatistics();
    } else {
      // For browse mode, do a full refresh (which includes statistics)
      await Promise.all([refresh(), refreshStatistics()]);
    }
  };

  const handleApproveClick = (affiliate: AffiliateWithStats) => {
    setAffiliateToApprove(affiliate);
    setReferralCode(affiliate.referralCode || '');
    setShowApproveModal(true);
  };

  const handleApproveConfirm = async () => {
    if (!affiliateToApprove) return;
    
    if (!referralCode.trim()) {
      toast.error('Please enter a referral code');
      return;
    }

    try {
      setIsApproving(affiliateToApprove.id);
      await approveAffiliateApplication(affiliateToApprove.id, referralCode.trim(), 'admin');
      toast.success(`Approved ${affiliateToApprove.userName}'s application`);
      await refreshAffiliatesAfterUpdate(); // Use the comprehensive refresh
      setShowApproveModal(false);
      setAffiliateToApprove(null);
      setReferralCode('');
    } catch (error) {
      console.error('Error approving affiliate:', error);
      toast.error('Failed to approve affiliate application');
    } finally {
      setIsApproving(null);
    }
  };

  const handleRejectClick = (affiliate: AffiliateWithStats) => {
    setAffiliateToReject(affiliate);
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!affiliateToReject) return;

    try {
      setIsRejecting(affiliateToReject.id);
      await rejectAffiliateApplication(affiliateToReject.id, 'admin');
      toast.success(`Rejected ${affiliateToReject.userName}'s application`);
      await refreshAffiliatesAfterUpdate(); // Use the comprehensive refresh
      setShowRejectModal(false);
      setAffiliateToReject(null);
    } catch (error) {
      console.error('Error rejecting affiliate:', error);
      toast.error('Failed to reject affiliate application');
    } finally {
      setIsRejecting(null);
    }
  };

  // Helper function to get previous month date range
  const getPreviousMonthDateRange = () => {
    const now = new Date();
    const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
    const month = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
    
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999);
    
    return { startDate, endDate };
  };

  // Helper function to filter commissions for previous month only
  const filterPreviousMonthCommissions = (commissions: any[]) => {
    const { startDate, endDate } = getPreviousMonthDateRange();
    
    return commissions.filter(commission => {
      const commissionDate = new Date(commission.createdAt);
      return commissionDate >= startDate && commissionDate <= endDate;
    });
  };

  // Helper function to check if affiliate already received payout for previous month
  const checkExistingPayout = async (affiliateId: string) => {
    try {
      const { startDate, endDate } = getPreviousMonthDateRange();
      const payouts = await getAffiliatePayouts(affiliateId);
      
      const existingPayout = payouts.find(payout => {
        const payoutDate = new Date(payout.paidAt);
        // Check if payout was made in the current month (for previous month's earnings)
        const currentMonth = new Date();
        const payoutInCurrentMonth = payoutDate.getMonth() === currentMonth.getMonth() && 
                                   payoutDate.getFullYear() === currentMonth.getFullYear();
        return payoutInCurrentMonth;
      });

      return {
        hasExisting: !!existingPayout,
        amount: existingPayout?.amount || 0,
        date: existingPayout?.paidAt || null
      };
    } catch (error) {
      console.error('Error checking existing payout:', error);
      return { hasExisting: false, amount: 0, date: null };
    }
  };

  // Load payout preview data for the selected affiliate
  const loadPayoutPreviewData = async (affiliate: AffiliateWithStats) => {
    setLoadingPayoutPreview(true);
    try {
      // Get all commissions for this affiliate
      const allCommissions = await getAffiliateCommissions(affiliate.id);
      
      // Filter for previous month only - show ALL commissions from previous month
      const prevMonthCommissions = filterPreviousMonthCommissions(allCommissions);
      
      // Check for existing payout
      const existingPayoutInfo = await checkExistingPayout(affiliate.id);
      
      // Calculate total amount from ALL previous month commissions (regardless of status)
      const totalAmount = prevMonthCommissions.reduce(
        (sum, commission) => sum + commission.commissionAmount, 
        0
      );

      // Store all previous month commissions for display
      setPreviousMonthCommissions(prevMonthCommissions);
      setPayoutPreviewData({
        totalAmount, // All previous month commissions amount for payout
        commissionCount: prevMonthCommissions.length, // All previous month commissions count
        hasExistingPayout: existingPayoutInfo.hasExisting,
        previousPayoutAmount: existingPayoutInfo.amount,
        previousPayoutDate: existingPayoutInfo.date
      });
    } catch (error) {
      console.error('Error loading payout preview data:', error);
      toast.error('Failed to load payout preview data');
    } finally {
      setLoadingPayoutPreview(false);
    }
  };

  // Load previous month commission data for affiliate details
  const loadAffiliatePrevMonthData = async (affiliateId: string) => {
    try {
      // Get all commissions for this affiliate
      const allCommissions = await getAffiliateCommissions(affiliateId);
      
      // Filter for previous month only
      const prevMonthCommissions = filterPreviousMonthCommissions(allCommissions);
      
      // Calculate total amount from all previous month commissions
      const totalAmount = prevMonthCommissions.reduce(
        (sum, commission) => sum + commission.commissionAmount,
        0
      );
      
      // Check if all previous month commissions have been paid by checking their status
      const allCommissionsPaid = prevMonthCommissions.length > 0 && 
        prevMonthCommissions.every(commission => commission.status === 'paid');
      
      // Update state for this affiliate
      setAffiliatePrevMonthData(prev => ({
        ...prev,
        [affiliateId]: {
          amount: totalAmount,
          isPaid: allCommissionsPaid && totalAmount > 0
        }
      }));
    } catch (error) {
      console.error('Error loading affiliate previous month data:', error);
    }
  };

  const handleTriggerPayout = async (affiliate: AffiliateWithStats) => {
    // Set the selected affiliate and open the preview modal
    setSelectedAffiliateForPayout(affiliate);
    setShowPayoutPreviewModal(true);
    
    // Load the payout preview data
    await loadPayoutPreviewData(affiliate);
  };

  // Handle the actual payout confirmation and execution
  const handlePayoutConfirm = async () => {
    if (!selectedAffiliateForPayout || payoutPreviewData.totalAmount <= 0) {
      toast.error('No valid payout amount');
      return;
    }

    if (payoutPreviewData.hasExistingPayout) {
      toast.error('Payout already processed for this affiliate this month');
      return;
    }

    setShowPayoutConfirmation(true);
  };

  // Execute the final payout after confirmation
  const executePayoutConfirm = async () => {
    if (!selectedAffiliateForPayout) return;

    try {
      setLoadingPayouts(true);
      
      // Trigger the payout with enhanced service function (will be updated)
      await triggerAffiliatePayout(selectedAffiliateForPayout.id, payoutPreviewData.totalAmount);
      
      toast.success(
        `Successfully processed payout of ${formatCurrency(payoutPreviewData.totalAmount)} for ${selectedAffiliateForPayout.userName}`
      );
      
      // Close modals and refresh data
      setShowPayoutConfirmation(false);
      setShowPayoutPreviewModal(false);
      
      // Refresh the previous month data for the affiliate details if it's currently selected
      if (selectedAffiliate && selectedAffiliate.id === selectedAffiliateForPayout.id) {
        await loadAffiliatePrevMonthData(selectedAffiliate.id);
      }
      
      setSelectedAffiliateForPayout(null);
      await handleRefresh();
      
    } catch (error) {
      console.error('Error executing payout:', error);
      toast.error('Failed to process payout');
    } finally {
      setLoadingPayouts(false);
    }
  };

  const copyReferralLink = (referralCode: string) => {
    const link = `https://jampackd.store?ref=${referralCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied to clipboard');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Approved</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Rejected</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Extract individual statistics values with fallbacks
  const totalAffiliates = statistics?.totalAffiliates ?? 0;
  const pendingApplications = statistics?.pendingApplications ?? 0;
  const approvedAffiliates = statistics?.approvedAffiliates ?? 0;
  const totalCommissionsPaid = statistics?.totalCommissionsPaid ?? 0;
  const newAffiliatesThisMonth = statistics?.newAffiliatesThisMonth ?? 0;
  const monthlyCommissions = statistics?.monthlyCommissions ?? 0;

  // Helper function to format percentage changes
  const formatPercentageChange = (change: number) => {
    if (change === 0) return null;
    const isPositive = change > 0;
    return (
      <span className={`text-sm ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
        {isPositive ? '+' : ''}{change.toFixed(1)}%
      </span>
    );
  };

  // Helper function to get date range text for time periods
  const getDateRangeText = (period: TimePeriod): string => {
    const now = new Date();
    
    switch (period) {
      case 'all_time':
        return 'All Time';
      case 'today':
        return now.toLocaleDateString();
      case 'this_week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        const weekEnd = new Date(now);
        return `${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()}`;
      case 'this_month':
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        return `${monthStart.toLocaleDateString()} - ${monthEnd.toLocaleDateString()}`;
      case 'this_quarter':
        const quarterStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
        const quarterEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
        return `${quarterStart.toLocaleDateString()} - ${quarterEnd.toLocaleDateString()}`;
      case 'this_year':
        const yearStart = new Date(now.getFullYear(), 0, 1);
        const yearEnd = new Date(now.getFullYear(), 11, 31);
        return `${yearStart.toLocaleDateString()} - ${yearEnd.toLocaleDateString()}`;
      default:
        return 'All Time';
    }
  };

  // Time period options
  const timePeriodOptions = [
    { value: 'all_time', label: 'All Time', dateRange: getDateRangeText('all_time') },
    { value: 'today', label: 'Today', dateRange: getDateRangeText('today') },
    { value: 'this_week', label: 'This Week', dateRange: getDateRangeText('this_week') },
    { value: 'this_month', label: 'This Month', dateRange: getDateRangeText('this_month') },
    { value: 'this_quarter', label: 'This Quarter', dateRange: getDateRangeText('this_quarter') },
    { value: 'this_year', label: 'This Year', dateRange: getDateRangeText('this_year') },
  ] as const;

  // Affiliate statistics modal functions
  const handleViewStatsClick = async (affiliate: AffiliateWithStats) => {
    setSelectedAffiliateForStats(affiliate);
    setShowStatsModal(true);
    setStatsDateFilter({ startDate: '', endDate: '' });
    setStatsActiveFilter(null);
    await loadAffiliateStatsData(affiliate.id);
  };

  // Load paginated commission records
  const loadCommissionRecordsPage = async (affiliateId: string, page: number, startDate?: Date, endDate?: Date) => {
    try {
      setLoadingAffiliateStats(true);
      
      // For Firebase cursor pagination, we need to track pages differently
      let lastDoc = null;
      
      if (page === 1) {
        // Reset pagination for first page
        setCommissionsPages(new Map());
        lastDoc = null;
      } else {
        // Get the last document for the previous page
        const prevPageLastDoc = commissionsPages.get(page - 1);
        lastDoc = prevPageLastDoc || null;
      }

      // Use the new paginated Firebase function
      const result: PaginatedCommissionsResult = await getAffiliateCommissionsPaginated(
        affiliateId,
        commissionsPageSize,
        lastDoc,
        startDate,
        endDate
      );

      // Update state
      setAffiliateCommissions(result.commissions);
      setCommissionsTotalCount(result.total);
      setCommissionsCurrentPage(page);
      setCommissionsHasMore(result.hasMore);
      
      // Store the last document for this page for future pagination
      if (result.lastDoc) {
        const newPages = new Map(commissionsPages);
        newPages.set(page, result.lastDoc);
        setCommissionsPages(newPages);
      }
    } catch (error) {
      console.error('Error loading commission records page:', error);
      toast.error('Failed to load commission records');
    } finally {
      setLoadingAffiliateStats(false);
    }
  };

  // Load paginated payout history
  const loadPayoutHistoryPage = async (affiliateId: string, page: number, startDate?: Date, endDate?: Date) => {
    try {
      setLoadingAffiliateStats(true);
      
      // For Firebase cursor pagination, we need to track pages differently
      let lastDoc = null;
      
      if (page === 1) {
        // Reset pagination for first page
        setPayoutsPages(new Map());
        lastDoc = null;
      } else {
        // Get the last document for the previous page
        const prevPageLastDoc = payoutsPages.get(page - 1);
        lastDoc = prevPageLastDoc || null;
      }

      // Use the new paginated Firebase function
      const result: PaginatedPayoutsResult = await getAffiliatePayoutsPaginated(
        affiliateId,
        payoutsPageSize,
        lastDoc,
        startDate,
        endDate
      );

      // Update state
      setAffiliatePayouts(result.payouts);
      setPayoutsTotalCount(result.total);
      setPayoutsCurrentPage(page);
      setPayoutsHasMore(result.hasMore);
      
      // Store the last document for this page for future pagination
      if (result.lastDoc) {
        const newPages = new Map(payoutsPages);
        newPages.set(page, result.lastDoc);
        setPayoutsPages(newPages);
      }
    } catch (error) {
      console.error('Error loading payout history page:', error);
      toast.error('Failed to load payout history');
    } finally {
      setLoadingAffiliateStats(false);
    }
  };

  const loadAffiliateStatsData = async (affiliateId: string, startDate?: Date, endDate?: Date) => {
    try {
      setLoadingAffiliateStats(true);
      
      // Reset pagination when loading new data
      setCommissionsCurrentPage(1);
      setPayoutsCurrentPage(1);
      
      // Get all data for stats calculation
      const [commissions, payouts] = await Promise.all([
        getAffiliateCommissions(affiliateId),
        getAffiliatePayouts(affiliateId)
      ]);

      let filteredCommissions = commissions;
      let filteredPayouts = payouts;

      // Apply date filtering if provided
      if (startDate && endDate) {
        filteredCommissions = commissions.filter(commission => {
          const commissionDate = new Date(commission.createdAt);
          return commissionDate >= startDate && commissionDate <= endDate;
        });

        filteredPayouts = payouts.filter(payout => {
          const payoutDate = new Date(payout.paidAt);
          return payoutDate >= startDate && payoutDate <= endDate;
        });
      }

      // Load first page of commission records and payout history
      await Promise.all([
        loadCommissionRecordsPage(affiliateId, 1, startDate, endDate),
        loadPayoutHistoryPage(affiliateId, 1, startDate, endDate)
      ]);

      // Calculate stats from filtered data
      const totalEarnings = filteredCommissions.reduce((sum, commission) => sum + commission.commissionAmount, 0);
      const totalOrderValue = filteredCommissions.reduce((sum, commission) => sum + commission.orderValue, 0);
      const avgOrderValue = filteredCommissions.length > 0 ? totalOrderValue / filteredCommissions.length : 0;

      // Get clicks and conversions for the same date range
      const [totalClicks, totalConversions] = await Promise.all([
        getAffiliateClicksInDateRange(affiliateId, startDate, endDate),
        getAffiliateConversionsInDateRange(affiliateId, startDate, endDate)
      ]);

      // Calculate conversion rate based on filtered data
      const conversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0;
      
      setAffiliateStats({
        totalClicks,
        totalConversions,
        totalEarnings,
        conversionRate,
        avgOrderValue
      });

    } catch (error) {
      console.error('Error loading affiliate stats data:', error);
      toast.error('Failed to load affiliate statistics');
    } finally {
      setLoadingAffiliateStats(false);
    }
  };

  const handleStatsDateFilterChange = async () => {
    if (!selectedAffiliateForStats || !statsDateFilter.startDate || !statsDateFilter.endDate) return;

    const startDate = new Date(statsDateFilter.startDate + 'T00:00:00');
    const endDate = new Date(statsDateFilter.endDate + 'T23:59:59');

    await loadAffiliateStatsData(selectedAffiliateForStats.id, startDate, endDate);
  };

  const handleStatsPresetFilter = async (preset: string | null) => {
    if (!selectedAffiliateForStats) return;

    setStatsActiveFilter(preset);
    setStatsDateFilter({ startDate: '', endDate: '' });
    
    if (!preset) {
      // Clear filter - show all time data
      await loadAffiliateStatsData(selectedAffiliateForStats.id);
      return;
    }

    const today = new Date();
    let startDate: Date;
    let endDate: Date = today;

    switch (preset) {
      case 'week':
        // This week starting from Sunday
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        startDate = weekStart;
        break;
      case 'month':
        // This month from beginning
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'quarter':
        // This quarter from beginning
        const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1);
        startDate = quarterStart;
        break;
      case 'year':
        // This year from beginning
        startDate = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        return;
    }

    await loadAffiliateStatsData(selectedAffiliateForStats.id, startDate, endDate);
  };

  const clearStatsFilter = async () => {
    if (!selectedAffiliateForStats) return;

    setStatsDateFilter({ startDate: '', endDate: '' });
    setStatsActiveFilter(null);
    await loadAffiliateStatsData(selectedAffiliateForStats.id);
  };

  // Pagination handlers
  const handleCommissionsPageChange = async (page: number) => {
    if (!selectedAffiliateForStats) return;
    
    const startDate = statsActiveFilter && statsDateFilter.startDate ? new Date(statsDateFilter.startDate) : undefined;
    const endDate = statsActiveFilter && statsDateFilter.endDate ? new Date(statsDateFilter.endDate) : undefined;
    
    await loadCommissionRecordsPage(selectedAffiliateForStats.id, page, startDate, endDate);
  };

  const handlePayoutsPageChange = async (page: number) => {
    if (!selectedAffiliateForStats) return;
    
    const startDate = statsActiveFilter && statsDateFilter.startDate ? new Date(statsDateFilter.startDate) : undefined;
    const endDate = statsActiveFilter && statsDateFilter.endDate ? new Date(statsDateFilter.endDate) : undefined;
    
    await loadPayoutHistoryPage(selectedAffiliateForStats.id, page, startDate, endDate);
  };

  const handleStatsCustomDateFilter = async () => {
    if (!selectedAffiliateForStats || !statsDateFilter.startDate || !statsDateFilter.endDate) return;
    
    const startDate = new Date(statsDateFilter.startDate);
    const endDate = new Date(statsDateFilter.endDate);
    
    if (startDate > endDate) {
      toast.error('Start date must be before end date');
      return;
    }
    
    setStatsActiveFilter('custom');
    await loadAffiliateStatsData(selectedAffiliateForStats.id, startDate, endDate);
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const getSocialIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return <Instagram className="h-4 w-4" />;
      case 'tiktok':
        return <Music className="h-4 w-4" />;
      case 'youtube':
        return <Youtube className="h-4 w-4" />;
      case 'facebook':
        return <Facebook className="h-4 w-4" />;
      case 'twitter':
      case 'twitter/x':
        return <Twitter className="h-4 w-4" />;
      case 'linkedin':
        return <Linkedin className="h-4 w-4" />;
      case 'pinterest':
        return <Bookmark className="h-4 w-4" />;
      case 'snapchat':
        return <Camera className="h-4 w-4" />;
      case 'blog':
        return <FileText className="h-4 w-4" />;
      case 'website':
        return <Globe className="h-4 w-4" />;
      case 'other':
        return <Globe className="h-4 w-4" />;
      default:
        return <Globe className="h-4 w-4" />;
    }
  };

  const getSocialUrl = (platform: string, handle: string) => {
    switch (platform.toLowerCase()) {
      case 'instagram':
        return `https://www.instagram.com/${handle.replace('@', '')}`;
      case 'tiktok':
        return `https://www.tiktok.com/@${handle.replace('@', '')}`;
      case 'youtube':
        return `https://www.youtube.com/@${handle.replace('@', '')}`;
      case 'facebook':
        return `https://www.facebook.com/${handle.replace('@', '')}`;
      case 'twitter':
      case 'twitter/x':
        return `https://www.twitter.com/${handle.replace('@', '')}`;
      case 'linkedin':
        return `https://www.linkedin.com/in/${handle.replace('@', '')}`;
      case 'pinterest':
        return `https://www.pinterest.com/${handle.replace('@', '')}`;
      case 'snapchat':
        return `https://www.snapchat.com/add/${handle.replace('@', '')}`;
      case 'blog':
      case 'website':
      case 'other':
        return handle.startsWith('http') ? handle : `https://${handle}`;
      default:
        return handle.startsWith('http') ? handle : `https://${handle}`;
    }
  };

  const getPlatformDisplayName = (profile: SocialProfile) => {
    if (profile.platform === 'Other' && profile.otherPlatform) {
      return profile.otherPlatform;
    }
    return profile.platform;
  };

  const getPlatformHandle = (profile: SocialProfile) => {
    if (profile.platform === 'Other') {
      return profile.handle.toLowerCase(); // For "Other", handle contains the URL in lowercase
    }
    return `@${profile.handle}`;
  };

  const renderActionButtons = (affiliate: AffiliateWithStats) => {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => {
            setSelectedAffiliate(affiliate);
            loadAffiliatePrevMonthData(affiliate.id);
          }}>
            <Eye className="h-4 w-4 mr-2" />
            View Details
          </DropdownMenuItem>
          {affiliate.status === 'approved' && (
            <DropdownMenuItem onClick={() => handleViewStatsClick(affiliate)}>
              <BarChart3 className="h-4 w-4 mr-2" />
              View Statistics
            </DropdownMenuItem>
          )}
          {affiliate.status === 'pending' && (
            <>
              <DropdownMenuItem 
                onClick={() => handleApproveClick(affiliate)}
                disabled={isApproving === affiliate.id}
                className="text-green-600"
              >
                {isApproving === affiliate.id ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Approve
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => handleRejectClick(affiliate)}
                disabled={isRejecting === affiliate.id}
                className="text-red-600"
              >
                {isRejecting === affiliate.id ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                Reject
              </DropdownMenuItem>
            </>
          )}
          {affiliate.status === 'approved' && (
            <>
              <DropdownMenuItem onClick={() => copyReferralLink(affiliate.referralCode!)}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Referral Link
              </DropdownMenuItem>
              {affiliate.stats.totalEarnings > 0 && (
                <DropdownMenuItem onClick={() => handleTriggerPayout(affiliate)}>
                  <CreditCard className="h-4 w-4 mr-2" />
                  Trigger Payout
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="space-y-6">
      {/* Action Button */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex justify-between items-center"
      >
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={loading || statisticsLoading}
            className="flex items-center gap-2"
          >
            {loading || statisticsLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            Refresh
          </Button>
          
          <Select value={selectedTimePeriod} onValueChange={(value: TimePeriod) => setSelectedTimePeriod(value)}>
            <SelectTrigger className="w-44 bg-white border-gray-200 hover:border-jamaica-green focus:border-jamaica-green focus:ring-jamaica-green/20 transition-colors">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <SelectValue placeholder="Select time period" />
              </div>
            </SelectTrigger>
            <SelectContent className="bg-white border-gray-200 shadow-lg">
              {timePeriodOptions.map((option) => (
                <SelectItem 
                  key={option.value} 
                  value={option.value}
                  className="hover:bg-jamaica-green/5 focus:bg-jamaica-green/10 cursor-pointer"
                >
                  <span className="font-medium text-gray-900">{option.label}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleCommissionSettingsClick}
            className="bg-white hover:bg-gray-50 flex items-center gap-2"
          >
            <DollarSign className="h-4 w-4" />
            Commission Settings
          </Button>
          <Button 
            onClick={handlePayoutSettingsClick}
            className="bg-jamaica-green hover:bg-jamaica-green/90 flex items-center gap-2"
          >
            <Calendar className="h-4 w-4" />
            Payout Settings
          </Button>
        </div>
      </motion.div>

      {/* Date Range Display */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
      >
        <div className="flex items-center text-sm text-gray-600">
          <span className="text-gray-700">{getDateRangeText(selectedTimePeriod)}</span>
        </div>
      </motion.div>

      {/* Statistics Error Display */}
      {statisticsError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-50 border border-red-200 rounded-lg p-4"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <p className="text-red-800 font-medium">Statistics Error</p>
          </div>
          <p className="text-red-700 mt-1">{statisticsError}</p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={refreshStatistics}
            className="mt-3 border-red-300 text-red-700 hover:bg-red-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Retry
          </Button>
        </motion.div>
      )}

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
      >
        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Affiliates
                </p>
                {statisticsLoading ? (
                  <div className="h-8 w-16 bg-gray-200 rounded animate-pulse mt-1"></div>
                ) : (
                  <p className="text-2xl font-bold text-gray-900">{totalAffiliates}</p>
                )}
              </div>
              <div className="h-12 w-12 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-jamaica-green" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm justify-between">
              <span className="text-gray-500">All affiliate members</span>
              {!statisticsLoading && percentageChanges && formatPercentageChange(percentageChanges.totalAffiliates)}
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Pending Applications
                </p>
                {statisticsLoading ? (
                  <div className="h-8 w-12 bg-gray-200 rounded animate-pulse mt-1"></div>
                ) : (
                  <p className="text-2xl font-bold text-yellow-600">{pendingApplications}</p>
                )}
              </div>
              <div className="h-12 w-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm justify-between">
              <span className="text-gray-600">Awaiting review</span>
              {!statisticsLoading && percentageChanges && formatPercentageChange(percentageChanges.pendingApplications)}
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Approved Affiliates
                </p>
                {statisticsLoading ? (
                  <div className="h-8 w-12 bg-gray-200 rounded animate-pulse mt-1"></div>
                ) : (
                  <p className="text-2xl font-bold text-green-600">{approvedAffiliates}</p>
                )}
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm justify-between">
              <span className="text-gray-600">Active members</span>
              {!statisticsLoading && percentageChanges && formatPercentageChange(percentageChanges.approvedAffiliates)}
            </div>
          </CardContent>
        </Card>

        <Card className="admin-card">
          <CardContent className="p-6 flex flex-col h-full">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  Total Paid
                </p>
                {statisticsLoading ? (
                  <div className="h-8 w-20 bg-gray-200 rounded animate-pulse mt-1"></div>
                ) : (
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(totalCommissionsPaid)}
                  </p>
                )}
              </div>
              <div className="h-12 w-12 bg-jamaica-yellow/20 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-jamaica-yellow" />
              </div>
            </div>
            <div className="flex items-center mt-auto pt-4 text-sm justify-between">
              <span className="text-gray-500">Commission payouts</span>
              {!statisticsLoading && percentageChanges && formatPercentageChange(percentageChanges.totalCommissionsPaid)}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Commission Source Analytics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        {/* Source Breakdown Chart */}
        <Card className="admin-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-jamaica-green" />
              Commission Source Analytics
            </CardTitle>
            <CardDescription>
              Performance breakdown by commission source for {getDateRangeText(selectedTimePeriod)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sourceAnalyticsLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-jamaica-green" />
              </div>
            ) : sourceAnalytics ? (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <ExternalLink className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-blue-800">Referral Links</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-900">{formatCurrency(sourceAnalytics.linkStats.totalEarnings)}</p>
                    <p className="text-sm text-blue-700">{sourceAnalytics.linkStats.count} commissions</p>
                    <p className="text-xs text-blue-600 mt-1">
                      {sourceAnalytics.summary.linkEarningsPercentage.toFixed(1)}% of total earnings
                    </p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Tag className="w-4 h-4 text-purple-600" />
                      <span className="text-sm font-medium text-purple-800">Discount Codes</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-900">{formatCurrency(sourceAnalytics.discountStats.totalEarnings)}</p>
                    <p className="text-sm text-purple-700">{sourceAnalytics.discountStats.count} commissions</p>
                    <p className="text-xs text-purple-600 mt-1">
                      {sourceAnalytics.summary.discountEarningsPercentage.toFixed(1)}% of total earnings
                    </p>
                  </div>
                </div>
                
                {/* Pie Chart */}
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Referral Links', value: sourceAnalytics.summary.linkEarningsPercentage, color: CHART_COLORS.link },
                          { name: 'Discount Codes', value: sourceAnalytics.summary.discountEarningsPercentage, color: CHART_COLORS.discount }
                        ]}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {[
                          { name: 'Referral Links', value: sourceAnalytics.summary.linkEarningsPercentage, color: CHART_COLORS.link },
                          { name: 'Discount Codes', value: sourceAnalytics.summary.discountEarningsPercentage, color: CHART_COLORS.discount }
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value: any) => [`${value.toFixed(1)}%`, 'Earnings Share']}
                        labelStyle={{ color: '#374151' }}
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #E5E7EB', 
                          borderRadius: '8px' 
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                No commission data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Performance Metrics */}
        <Card className="admin-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-jamaica-green" />
              Performance Metrics
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sourceAnalyticsLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded animate-pulse" />
                ))}
              </div>
            ) : sourceAnalytics ? (
              <div className="space-y-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800">Referral Links</p>
                  <p className="text-lg font-bold text-blue-900">{formatCurrency(sourceAnalytics.linkStats.avgCommission)}</p>
                  <p className="text-xs text-blue-600">Avg. Commission</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-sm font-medium text-purple-800">Discount Codes</p>
                  <p className="text-lg font-bold text-purple-900">{formatCurrency(sourceAnalytics.discountStats.avgCommission)}</p>
                  <p className="text-xs text-purple-600">Avg. Commission</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-sm font-medium text-green-800">Link AOV</p>
                  <p className="text-lg font-bold text-green-900">{formatCurrency(sourceAnalytics.linkStats.avgOrderValue)}</p>
                  <p className="text-xs text-green-600">Average Order Value</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg">
                  <p className="text-sm font-medium text-orange-800">Discount AOV</p>
                  <p className="text-lg font-bold text-orange-900">{formatCurrency(sourceAnalytics.discountStats.avgOrderValue)}</p>
                  <p className="text-xs text-orange-600">Average Order Value</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-48 text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Top Performing Discount Codes */}
      {discountPerformance && discountPerformance.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
        >
          <Card className="admin-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="h-5 w-5 text-jamaica-green" />
                Top Performing Affiliate Discount Codes
              </CardTitle>
              <CardDescription>
                Discount codes generating the most affiliate commissions for {getDateRangeText(selectedTimePeriod)}
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Discount Code</TableHead>
                      <TableHead>Commissions</TableHead>
                      <TableHead>Total Earnings</TableHead>
                      <TableHead>Order Value</TableHead>
                      <TableHead>Unique Affiliates</TableHead>
                      <TableHead>Avg. Commission</TableHead>
                      <TableHead>Avg. Order Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {discountPerformanceLoading ? (
                      Array.from({ length: 3 }).map((_, index) => (
                        <TableRow key={index}>
                          {Array.from({ length: 7 }).map((_, cellIndex) => (
                            <TableCell key={cellIndex}>
                              <div className="h-4 bg-gray-200 rounded animate-pulse" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      discountPerformance.slice(0, 10).map((code, index) => (
                        <TableRow key={code.discountCodeId} className="hover:bg-gray-50">
                          <TableCell className="font-mono text-sm">{code.discountCodeId}</TableCell>
                          <TableCell>{code.commissionCount}</TableCell>
                          <TableCell className="font-semibold text-green-600">
                            {formatCurrency(code.totalEarnings)}
                          </TableCell>
                          <TableCell>{formatCurrency(code.totalOrderValue)}</TableCell>
                          <TableCell>{code.uniqueAffiliates}</TableCell>
                          <TableCell>{formatCurrency(code.avgCommission)}</TableCell>
                          <TableCell>{formatCurrency(code.avgOrderValue)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="bg-white rounded-xl shadow-md p-6 border border-gray-100"
      >
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search affiliates by name, email, or referral code..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 bg-gray-50 border-0 focus:bg-white focus:ring-2 focus:ring-jamaica-green/20"
              />
              {(searchLoading || filterLoading || sortLoading) && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader2 className="w-4 h-4 animate-spin text-jamaica-green" />
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <AffiliateSort
              sortField={sortField}
              sortOrder={sortOrder}
              onSortChange={setSorting}
            />
            
            <Button
              variant={showFilters ? "default" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2"
            >
              <Filter className="w-4 h-4" />
              Filters
              {selectedStatus !== 'all' && (
                <Badge
                  className={`ml-1 border-transparent ${
                    showFilters
                      ? "bg-white text-jamaica-green hover:bg-white hover:text-jamaica-green"
                      : "bg-jamaica-green text-white hover:bg-jamaica-green hover:text-white"
                  }`}
                >
                  1
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Status Filter */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="mt-4 pt-4 border-t border-gray-200"
          >
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-sm font-medium text-gray-700 mr-2">
                Status:
              </span>
              {(['all', 'pending', 'approved', 'rejected'] as const).map((status) => (
                <Button
                  key={status}
                  variant={selectedStatus === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedStatus(status)}
                  className={
                    selectedStatus === status
                      ? status === 'pending' ? "bg-yellow-100 text-yellow-800 hover:bg-yellow-100 hover:text-yellow-800"
                        : status === 'approved' ? "bg-green-100 text-green-800 hover:bg-green-100 hover:text-green-800"
                        : status === 'rejected' ? "bg-red-100 text-red-800 hover:bg-red-100 hover:text-red-800"
                        : "bg-jamaica-green text-white hover:bg-jamaica-green hover:text-white"
                      : ""
                  }
                >
                  {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>

            {/* Clear Filters */}
            {(selectedStatus !== 'all' || sortField) && (
              <div className="pt-2 border-t border-gray-100 mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedStatus('all');
                    setSorting(null, 'desc');
                  }}
                  className="text-jamaica-green hover:text-jamaica-green/90 hover:bg-jamaica-green/10"
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>

      {/* Affiliates Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <Card className="admin-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="h-5 w-5 text-jamaica-green" />
                Affiliates ({totalCount})
                {isCombinedMode && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-jamaica-black border-jamaica-black hover:bg-transparent hover:text-jamaica-black hover:border-jamaica-black"
                  >
                    Combined Mode
                  </Badge>
                )}
                {isSearchMode && !isCombinedMode && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-jamaica-green border-jamaica-green hover:bg-transparent hover:text-jamaica-green hover:border-jamaica-green"
                  >
                    Search Mode
                  </Badge>
                )}
                {isFilterMode && !isCombinedMode && !isSortMode && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-jamaica-yellow border-jamaica-yellow hover:bg-transparent hover:text-jamaica-yellow hover:border-jamaica-yellow"
                  >
                    Filter Mode
                  </Badge>
                )}
                {isSortMode && !isSearchMode && !isFilterMode && (
                  <Badge
                    variant="outline"
                    className="ml-2 text-purple-600 border-purple-600 hover:bg-transparent hover:text-purple-600 hover:border-purple-600"
                  >
                    Sort Mode
                  </Badge>
                )}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading || searchLoading || filterLoading || sortLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
                <span className="ml-2 text-gray-600">
                  {searchLoading
                    ? "Searching affiliates..."
                    : filterLoading
                    ? "Filtering affiliates..."
                    : sortLoading
                    ? "Sorting affiliates..."
                    : "Loading affiliates..."}
                </span>
              </div>
            ) : affiliates.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <UserPlus className="h-12 w-12 text-gray-400 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  No Affiliates Found
                </h3>
                <p className="text-gray-600 mb-4">
                  {isCombinedMode
                    ? "No affiliates match your search query and filters."
                    : isSearchMode
                    ? "No affiliates match your search criteria."
                    : isFilterMode
                    ? "No affiliates match the selected filters."
                    : "No affiliate applications yet."}
                </p>
                {(searchQuery ||
                  isFilterMode ||
                  sortField) && (
                  <div className="space-y-2">
                    {searchQuery && (
                      <Button
                        onClick={() => handleSearch("")}
                        variant="outline"
                      >
                        Clear Search
                      </Button>
                    )}
                    {(isFilterMode || sortField) && (
                      <Button
                        onClick={() => {
                          setSelectedStatus('all');
                          setSorting(null, 'desc');
                        }}
                        variant="outline"
                      >
                        Clear Filters & Sort
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Name</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Applied</TableHead>
                        <TableHead>Performance (All-Time)</TableHead>
                        <TableHead>Earnings (All-Time)</TableHead>
                        <TableHead className="w-16"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {affiliates.map((affiliate, index) => (
                        <motion.tr
                          key={affiliate.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-full flex items-center justify-center text-white font-medium">
                                {getInitials(affiliate.userName)}
                              </div>
                              <div className="space-y-1">
                                <p className="font-medium text-gray-900">{affiliate.userName}</p>
                                <p className="text-sm text-gray-500">{affiliate.userEmail}</p>
                                {affiliate.referralCode && (
                                  <p className="text-xs text-jamaica-green font-mono">
                                    {affiliate.referralCode}
                                  </p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(affiliate.status)}
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-gray-400" />
                                <p className="text-sm text-gray-600">
                                  {formatDate(affiliate.submittedAt)}
                                </p>
                              </div>
                              {affiliate.reviewedAt && (
                                <div className="text-xs text-gray-500">
                                  Reviewed {formatDate(affiliate.reviewedAt)}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            {/* All-time performance metrics - never filtered by date */}
                            <div className="space-y-1">
                              <div className="text-sm text-gray-900">
                                {affiliate.stats.totalClicks} clicks
                              </div>
                              <div className="text-sm text-gray-900">
                                {affiliate.stats.totalConversions} conversions
                              </div>
                              <div className="text-xs text-gray-500">
                                {affiliate.stats.conversionRate.toFixed(1)}% rate
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {/* All-time earnings - never filtered by date */}
                            <div className="font-semibold text-gray-900">
                              {formatCurrency(affiliate.stats.totalEarnings)}
                            </div>
                          </TableCell>
                          <TableCell>
                            {renderActionButtons(affiliate)}
                          </TableCell>
                        </motion.tr>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Pagination */}
                <div className="p-6 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    loading={loading || searchLoading || filterLoading || sortLoading}
                    totalItems={totalCount}
                    itemsPerPage={20}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Affiliate Details Modal */}
      <AnimatePresence>
        {selectedAffiliate && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
            style={{
              margin: 0,
              padding: "1rem",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: "spring",
                duration: 0.5,
                bounce: 0.3,
              }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Enhanced Header */}
              <div className="relative bg-gradient-to-r from-jamaica-green to-emerald-600 p-6 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/20 to-transparent"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                      className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                    >
                      <Eye className="h-6 w-6 text-white" />
                    </motion.div>
                    <div>
                      <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl font-bold text-white"
                      >
                        Affiliate Details
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-white/80 text-sm"
                      >
                        {selectedAffiliate.userName} • {selectedAffiliate.status}
                      </motion.p>
                    </div>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedAffiliate(null)}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
                <div className="p-6 space-y-6">
                  
                  {/* Overview Cards */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="grid grid-cols-1 md:grid-cols-4 gap-4"
                  >
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-gradient-to-br from-jamaica-green to-emerald-600 rounded-lg flex items-center justify-center">
                          <Users className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{selectedAffiliate.stats.totalClicks}</p>
                          <p className="text-sm text-gray-600">Total Clicks</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                          <TrendingUp className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{selectedAffiliate.stats.totalConversions}</p>
                          <p className="text-sm text-gray-600">Conversions</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                          <DollarSign className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{formatCurrency(selectedAffiliate.stats.totalEarnings)}</p>
                          <p className="text-sm text-gray-600">Total Earnings</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                          <RefreshCw className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-gray-900">{selectedAffiliate.stats.conversionRate.toFixed(1)}%</p>
                          <p className="text-sm text-gray-600">Conversion Rate</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Profile Information */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <UserPlus className="h-5 w-5 text-jamaica-green" />
                        Profile Information
                      </h3>
                    </div>
                    <div className="p-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="h-8 w-8 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                              <Mail className="h-4 w-4 text-jamaica-green" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Full Name</p>
                              <p className="font-semibold text-gray-900">{selectedAffiliate.userName}</p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="h-8 w-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                              <Mail className="h-4 w-4 text-blue-500" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Email Address</p>
                              <p className="font-semibold text-gray-900">{selectedAffiliate.userEmail}</p>
                            </div>
                          </div>
                        </div>
                        
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="h-8 w-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-600">Status</p>
                              <div className="mt-1">{getStatusBadge(selectedAffiliate.status)}</div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className="h-8 w-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
                              <Copy className="h-4 w-4 text-purple-500" />
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-600">Referral Code</p>
                              <p className="font-mono font-semibold text-gray-900">{selectedAffiliate.referralCode || 'Not assigned'}</p>
                            </div>
                            {selectedAffiliate.referralCode && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyReferralLink(selectedAffiliate.referralCode!)}
                                className="text-purple-600 hover:bg-purple-50"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Social Profiles */}
                  {selectedAffiliate.profiles.length > 0 && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.5 }}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                    >
                      <div className="p-4 bg-gray-50 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                          <TrendingUp className="h-5 w-5 text-jamaica-green" />
                          Social Media Presence
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          {selectedAffiliate.profiles.length} connected platform{selectedAffiliate.profiles.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                      <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {selectedAffiliate.profiles.map((profile, index) => (
                            <motion.div 
                              key={index}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.1 + index * 0.05 }}
                              className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl hover:shadow-md transition-all duration-200"
                            >
                              <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                {getSocialIcon(profile.platform)}
                              </div>
                              <div className="flex-1">
                                <div className="font-semibold text-gray-900 capitalize">{getPlatformDisplayName(profile)}</div>
                                <div className="text-sm text-gray-600 font-mono">{getPlatformHandle(profile)}</div>
                              </div>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                asChild
                                className="rounded-lg border-gray-300 hover:border-jamaica-green hover:text-jamaica-green transition-colors"
                              >
                                <a 
                                  href={getSocialUrl(profile.platform, profile.handle)} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2"
                                >
                                  <ExternalLink className="h-4 w-4" />
                                  Visit
                                </a>
                              </Button>
                            </motion.div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Website */}
                  {selectedAffiliate.website && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.6 }}
                      className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                    >
                      <div className="p-4 bg-gray-50 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                          <Globe className="h-5 w-5 text-jamaica-green" />
                          Website & Portfolio
                        </h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Primary website or blog
                        </p>
                      </div>
                      <div className="p-6">
                        <motion.div 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-4 p-4 bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl hover:shadow-md transition-all duration-200"
                        >
                          <div className="h-12 w-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Globe className="h-5 w-5 text-gray-600" />
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">Personal Website</div>
                            <div className="text-sm text-gray-600 break-all">
                              {selectedAffiliate.website}
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            asChild
                            className="rounded-lg border-gray-300 hover:border-jamaica-green hover:text-jamaica-green transition-colors"
                          >
                            <a 
                              href={selectedAffiliate.website.startsWith('http') ? selectedAffiliate.website : `https://${selectedAffiliate.website}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2"
                            >
                              <ExternalLink className="h-4 w-4" />
                              Visit
                            </a>
                          </Button>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}

                  {/* Payment Information */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <CreditCard className="h-5 w-5 text-jamaica-green" />
                        Payment Information
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Payout method and account details
                      </p>
                    </div>
                    <div className="p-6">
                      {selectedAffiliate.paymentInfo.type === 'jamaican_bank' ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-jamaica-green/5 to-emerald-50 border border-jamaica-green/20 rounded-xl">
                            <div className="h-12 w-12 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                              <CreditCard className="h-6 w-6 text-jamaica-green" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">Jamaican Bank Account</p>
                              <p className="text-sm text-gray-600">Complete banking details for payouts</p>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-3">
                              <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-sm font-medium text-gray-600">Bank Name</p>
                                <p className="font-semibold text-gray-900">{selectedAffiliate.paymentInfo.bankInfo?.bankName}</p>
                              </div>
                              <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-sm font-medium text-gray-600">Branch</p>
                                <p className="font-semibold text-gray-900">{selectedAffiliate.paymentInfo.bankInfo?.branch}</p>
                              </div>
                              <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-sm font-medium text-gray-600">Account Type</p>
                                <p className="font-semibold text-gray-900 capitalize">{selectedAffiliate.paymentInfo.bankInfo?.accountType}</p>
                              </div>
                            </div>
                            
                            <div className="space-y-3">
                              <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-sm font-medium text-gray-600">Account Holder Name</p>
                                <p className="font-semibold text-gray-900">{selectedAffiliate.paymentInfo.bankInfo?.accountHolderName}</p>
                              </div>
                              <div className="p-3 bg-gray-50 rounded-lg">
                                <p className="text-sm font-medium text-gray-600">Account Number</p>
                                <p className="font-mono text-gray-900 select-all">{selectedAffiliate.paymentInfo.bankInfo?.accountNumber}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                            <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                              <Mail className="h-6 w-6 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">PayPal Account</p>
                              <p className="text-sm text-gray-600">Email address for PayPal payouts</p>
                            </div>
                          </div>
                          
                          <div className="p-4 bg-gray-50 rounded-lg">
                            <p className="text-sm font-medium text-gray-600">PayPal Email</p>
                            <p className="font-semibold text-gray-900 select-all">{selectedAffiliate.paymentInfo.paypalEmail}</p>
                          </div>
                        </div>
                      )}
                      
                      {(() => {
                        const prevMonthData = affiliatePrevMonthData[selectedAffiliate.id];
                        const hasCommissions = prevMonthData && prevMonthData.amount > 0;
                        const isPaid = prevMonthData?.isPaid || false;
                        
                        if (!hasCommissions) return null;
                        
                        return (
                          <div className={`mt-6 p-4 rounded-xl border ${
                            isPaid 
                              ? 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-300' 
                              : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div>
                                <p className={`font-semibold ${isPaid ? 'text-gray-600' : 'text-gray-900'}`}>
                                  Previous Month's Commissions
                                </p>
                                <p className={`text-2xl font-bold ${
                                  isPaid ? 'text-gray-500' : 'text-green-600'
                                }`}>
                                  {formatCurrency(prevMonthData.amount)}
                                </p>
                              </div>
                              <Button
                                onClick={() => handleTriggerPayout(selectedAffiliate)}
                                disabled={loadingPayouts || isPaid}
                                className={`${
                                  isPaid
                                    ? 'bg-gray-400 hover:bg-gray-400 text-gray-600 cursor-not-allowed'
                                    : 'bg-jamaica-green hover:bg-jamaica-green/90 text-white'
                                }`}
                              >
                                {loadingPayouts ? (
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                ) : isPaid ? (
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                ) : (
                                  <CreditCard className="h-4 w-4 mr-2" />
                                )}
                                {isPaid ? 'Already Paid' : 'Trigger Payout'}
                              </Button>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Affiliate Statistics Modal */}
      <AnimatePresence>
        {showStatsModal && selectedAffiliateForStats && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
            style={{
              margin: 0,
              padding: "1rem",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: "spring",
                duration: 0.5,
                bounce: 0.3,
              }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Enhanced Header */}
              <div className="relative bg-gradient-to-r from-jamaica-green to-emerald-600 p-6 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/20 to-transparent"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                      className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                    >
                      <BarChart3 className="h-6 w-6 text-white" />
                    </motion.div>
                    <div>
                      <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl font-bold text-white"
                      >
                        Affiliate Statistics
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-white/80 text-sm"
                      >
                        {selectedAffiliateForStats.userName} - Performance Analytics
                      </motion.p>
                    </div>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowStatsModal(false)}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
                <div className="p-6 space-y-6">
                  
                  {/* Date Filter Section */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm"
                  >
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-jamaica-green" />
                            Date Range Filter
                            {statsActiveFilter && (
                              <div className="flex items-center gap-2 ml-2">
                                <Badge variant="outline" className="bg-jamaica-green/10 text-jamaica-green border-jamaica-green/30 text-xs">
                                  Active
                                </Badge>
                                {statsActiveFilter === 'custom' ? (
                                  <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300 text-xs">
                                    Custom Range
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300 text-xs">
                                    Preset Range
                                  </Badge>
                                )}
                              </div>
                            )}
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            {statsActiveFilter 
                              ? `Showing filtered results ${
                                  statsActiveFilter === 'custom' ? '(Custom Range)' :
                                  statsActiveFilter === 'month' ? '(This Month)' :
                                  statsActiveFilter === 'week' ? '(This Week)' :
                                  statsActiveFilter === 'quarter' ? '(This Quarter)' :
                                  statsActiveFilter === 'year' ? '(This Year)' : '(Preset)'
                                }`
                              : 'Filter statistics by date range'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex flex-wrap gap-2 mb-4">
                        {[
                          { key: null, label: 'All Time' },
                          { key: 'month', label: 'This Month' },
                          { key: 'week', label: 'This Week' },
                          { key: 'quarter', label: 'This Quarter' },
                          { key: 'year', label: 'This Year' }
                                                 ].map(({ key, label }) => (
                           <Button
                             key={key || 'all'}
                             variant={statsActiveFilter === key ? "default" : "outline"}
                             size="sm"
                             onClick={() => handleStatsPresetFilter(key)}
                             className={statsActiveFilter === key ? "bg-jamaica-green text-white" : ""}
                           >
                             {label}
                           </Button>
                         ))}
                      </div>
                      
                      <div className={`p-4 rounded-lg border-2 transition-all duration-300 ${
                        statsActiveFilter === 'custom' 
                          ? 'border-purple-200 bg-purple-50/50' 
                          : 'border-gray-200 bg-gray-50/50'
                      }`}>
                        <div className="flex items-center gap-2 mb-3">
                          <Label className="text-sm font-semibold text-gray-700">Custom Date Range</Label>
                          {statsActiveFilter === 'custom' && (
                            <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300 text-xs">
                              Active
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="start-date">Start Date</Label>
                            <Input
                              id="start-date"
                              type="date"
                              value={statsDateFilter.startDate}
                              onChange={(e) => {
                                setStatsDateFilter(prev => ({ ...prev, startDate: e.target.value }));
                                // Reset activeFilter when manually changing dates to indicate custom range
                                if (statsActiveFilter && statsActiveFilter !== 'custom') {
                                  setStatsActiveFilter(null);
                                }
                              }}
                              className="w-full"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="end-date">End Date</Label>
                            <Input
                              id="end-date"
                              type="date"
                              value={statsDateFilter.endDate}
                              onChange={(e) => {
                                setStatsDateFilter(prev => ({ ...prev, endDate: e.target.value }));
                                // Reset activeFilter when manually changing dates to indicate custom range
                                if (statsActiveFilter && statsActiveFilter !== 'custom') {
                                  setStatsActiveFilter(null);
                                }
                              }}
                              className="w-full"
                            />
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-4">
                                                 <Button
                           onClick={handleStatsCustomDateFilter}
                           disabled={!statsDateFilter.startDate || !statsDateFilter.endDate || loadingAffiliateStats}
                           className="bg-jamaica-green hover:bg-jamaica-green/90 text-white"
                         >
                           {loadingAffiliateStats ? (
                             <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                           ) : (
                             <Filter className="h-4 w-4 mr-2" />
                           )}
                           Apply Filter
                         </Button>
                         <Button
                           variant="outline"
                           onClick={clearStatsFilter}
                           disabled={loadingAffiliateStats}
                         >
                           Clear Filter
                         </Button>
                      </div>
                    </div>
                  </motion.div>

                  {/* Statistics Cards */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
                  >
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-blue-800">Total Clicks</p>
                            <p className="text-2xl font-bold text-blue-900">{affiliateStats.totalClicks}</p>
                          </div>
                          <div className="h-12 w-12 bg-blue-200 rounded-full flex items-center justify-center">
                            <Eye className="h-6 w-6 text-blue-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-green-800">Conversions</p>
                            <p className="text-2xl font-bold text-green-900">{affiliateStats.totalConversions}</p>
                          </div>
                          <div className="h-12 w-12 bg-green-200 rounded-full flex items-center justify-center">
                            <CheckCircle className="h-6 w-6 text-green-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-purple-800">Conversion Rate</p>
                            <p className="text-2xl font-bold text-purple-900">{affiliateStats.conversionRate.toFixed(1)}%</p>
                          </div>
                          <div className="h-12 w-12 bg-purple-200 rounded-full flex items-center justify-center">
                            <TrendingUp className="h-6 w-6 text-purple-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-orange-800">Total Earnings</p>
                            <p className="text-2xl font-bold text-orange-900">{formatCurrency(affiliateStats.totalEarnings)}</p>
                          </div>
                          <div className="h-12 w-12 bg-orange-200 rounded-full flex items-center justify-center">
                            <DollarSign className="h-6 w-6 text-orange-600" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Commission Records Table */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm"
                  >
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <DollarSign className="h-5 w-5 text-jamaica-green" />
                            Commission Records ({commissionsTotalCount})
                          </h3>
                          <p className="text-sm text-gray-600 mt-1">
                            Detailed commission earnings history (positive earnings & negative clawbacks)
                          </p>
                        </div>
                        
                        {/* Source Filter */}
                        <div className="flex items-center gap-2">
                          <Label htmlFor="source-filter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                            Source:
                          </Label>
                          <select
                            id="source-filter"
                            value={commissionSourceFilter}
                            onChange={(e) => setCommissionSourceFilter(e.target.value as 'all' | 'link' | 'discount_code')}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-jamaica-green/20 focus:border-jamaica-green"
                          >
                            <option value="all">All Sources</option>
                            <option value="link">Referral Links</option>
                            <option value="discount_code">Discount Codes</option>
                          </select>
                        </div>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead>Date</TableHead>
                            <TableHead>Order ID</TableHead>
                            <TableHead>Order Value</TableHead>
                            <TableHead>Order Status</TableHead>
                            <TableHead>Commission Rate</TableHead>
                            <TableHead>Commission Amount</TableHead>
                            <TableHead>Source</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {affiliateCommissions.length > 0 ? (
                            affiliateCommissions
                              .filter(commission => {
                                if (commissionSourceFilter === 'all') return true;
                                if (commissionSourceFilter === 'link') return commission.source === 'link' || !commission.source;
                                if (commissionSourceFilter === 'discount_code') return commission.source === 'discount_code';
                                return true;
                              })
                              .map((commission, index) => (
                              <TableRow key={commission.id || index}>
                                <TableCell>
                                  {new Date(commission.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell className="font-mono text-sm">
                                  {commission.orderId}
                                </TableCell>
                                <TableCell>{formatCurrency(commission.orderValue)}</TableCell>
                                <TableCell>
                                  <Badge className={
                                    commission.paymentStatus === 'succeeded' ? 'bg-green-100 text-green-800' :
                                    commission.paymentStatus === 'refunded' ? 'bg-red-100 text-red-800' :
                                    commission.paymentStatus === 'failed' ? 'bg-red-100 text-red-800' :
                                    commission.paymentStatus === 'refund_pending' ? 'bg-orange-100 text-orange-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }>
                                    {commission.paymentStatus === 'succeeded' ? 'Paid' : 
                                     commission.paymentStatus === 'refunded' ? 'Refunded' : 
                                     commission.paymentStatus || 'pending'}
                                  </Badge>
                                </TableCell>
                                <TableCell>{commission.commissionRate}%</TableCell>
                                <TableCell className={`font-semibold ${commission.commissionAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                  {formatCurrency(commission.commissionAmount)}
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {commission.source === 'discount_code' ? (
                                      <>
                                        <Tag className="w-4 h-4 text-purple-500" />
                                        <span className="text-sm font-medium text-purple-700">
                                          Discount Code
                                        </span>
                                      </>
                                    ) : (
                                      <>
                                        <ExternalLink className="w-4 h-4 text-blue-500" />
                                        <span className="text-sm font-medium text-blue-700">
                                          Referral Link
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <Badge className={
                                    commission.status === 'paid' ? 'bg-green-100 text-green-800' :
                                    commission.status === 'clawback' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }>
                                    {commission.status === 'clawback' 
                                      ? 'Clawback' 
                                      : commission.status?.charAt(0).toUpperCase() + (commission.status?.slice(1) || '')
                                    }
                                  </Badge>
                                </TableCell>
                              </TableRow>
                              ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                                {commissionSourceFilter === 'all' 
                                  ? 'No commission records found'
                                  : `No ${commissionSourceFilter === 'link' ? 'referral link' : 'discount code'} commission records found`
                                }
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Commission Records Pagination */}
                    <div className="p-6 border-t border-gray-200">
                      <Pagination
                        currentPage={commissionsCurrentPage}
                        totalPages={Math.ceil(commissionsTotalCount / 20)}
                        onPageChange={handleCommissionsPageChange}
                        loading={loadingAffiliateStats}
                        totalItems={commissionsTotalCount}
                        itemsPerPage={20}
                      />
                    </div>
                  </motion.div>

                  {/* Payout Records Table */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm"
                  >
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <CreditCard className="h-5 w-5 text-jamaica-green" />
                        Payout History ({payoutsTotalCount})
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Past payouts and transaction history
                      </p>
                    </div>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-gray-50">
                            <TableHead>Date</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Method</TableHead>
                            <TableHead>Transaction ID</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {affiliatePayouts.length > 0 ? (
                            affiliatePayouts.map((payout, index) => (
                              <TableRow key={payout.id || index}>
                                <TableCell>
                                  {new Date(payout.paidAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell className="font-semibold text-green-600">
                                  {formatCurrency(payout.amount)}
                                </TableCell>
                                <TableCell>{payout.method || 'PayPal'}</TableCell>
                                <TableCell className="font-mono text-sm">
                                  {payout.transactionId || 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <Badge className="bg-green-100 text-green-800">
                                    {payout.status}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                                No payout records found
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                    
                    {/* Payout History Pagination */}
                    <div className="p-6 border-t border-gray-200">
                      <Pagination
                        currentPage={payoutsCurrentPage}
                        totalPages={Math.ceil(payoutsTotalCount / 20)}
                        onPageChange={handlePayoutsPageChange}
                        loading={loadingAffiliateStats}
                        totalItems={payoutsTotalCount}
                        itemsPerPage={20}
                      />
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Approve Modal */}
      <AnimatePresence>
        {showApproveModal && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
            style={{
              margin: 0,
              padding: "1rem",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: "spring",
                duration: 0.5,
                bounce: 0.3,
              }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Enhanced Header */}
              <div className="relative bg-gradient-to-r from-green-600 to-emerald-600 p-6 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-green-600/20 to-transparent"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                      className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                    >
                      <CheckCircle className="h-6 w-6 text-white" />
                    </motion.div>
                    <div>
                      <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl font-bold text-white"
                      >
                        Approve Affiliate Application
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-white/80 text-sm"
                      >
                        Set referral code and approve application
                      </motion.p>
                    </div>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        !isApproving && setShowApproveModal(false)
                      }
                      disabled={isApproving === affiliateToApprove?.id}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-b from-gray-50/50 to-white">
                <div className="space-y-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-center"
                  >
                    <p className="text-gray-700 text-base leading-relaxed max-w-md mx-auto">
                      Enter a referral code for <strong>{affiliateToApprove?.userName}</strong>. This code will be used to track their referrals and they'll receive an approval email.
                    </p>
                  </motion.div>

                  {affiliateToApprove && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 p-6"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <UserPlus className="h-5 w-5 text-green-600" />
                        </div>
                        <h4 className="text-base font-semibold text-gray-900">
                          Affiliate Application Details
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <span className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <Mail className="h-4 w-4 text-green-500" />
                            Name
                          </span>
                          <p className="text-base font-semibold text-gray-900">
                            {affiliateToApprove.userName}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <Mail className="h-4 w-4 text-green-500" />
                            Email
                          </span>
                          <p className="text-base text-gray-900">
                            {affiliateToApprove.userEmail}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-green-500" />
                            Application Date
                          </span>
                          <p className="text-base text-gray-900">
                            {formatDate(affiliateToApprove.submittedAt)}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-green-500" />
                            Social Profiles
                          </span>
                          <p className="text-base text-gray-900">
                            {affiliateToApprove.profiles.length} platform{affiliateToApprove.profiles.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="space-y-4"
                  >
                    <label htmlFor="referral-code" className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <Copy className="h-4 w-4 text-green-500" />
                      Referral Code *
                    </label>
                    <Input
                      id="referral-code"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                      placeholder="Enter referral code (e.g., JAMAICA2024)"
                      className="h-12 text-base font-mono tracking-wider uppercase placeholder:normal-case placeholder:tracking-normal rounded-xl border-gray-200 focus:border-green-500 focus:ring-green-500/20 shadow-sm hover:shadow-md focus:shadow-lg transition-all duration-200"
                      disabled={isApproving === affiliateToApprove?.id}
                    />
                    {referralCode && (
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <p className="text-sm font-medium text-gray-600 mb-1">Preview Link:</p>
                        <p className="text-sm text-blue-600 font-mono break-all">
                          https://jampackd.store?ref={referralCode}
                        </p>
                      </div>
                    )}
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4"
                  >
                    <div className="flex items-start gap-3">
                      <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-green-800">
                          Approval Actions
                        </p>
                        <ul className="text-sm text-green-700 mt-1 space-y-1">
                          <li>• Activate affiliate account immediately</li>
                          <li>• Send approval email with referral link</li>
                          <li>• Enable commission tracking</li>
                          <li>• Grant access to affiliate dashboard</li>
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Enhanced Footer */}
              <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
                <div className="flex items-center justify-end gap-4">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      variant="outline"
                      onClick={() => setShowApproveModal(false)}
                      disabled={isApproving === affiliateToApprove?.id}
                      className="px-6 h-11 rounded-xl font-medium"
                    >
                      Cancel
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={handleApproveConfirm}
                      disabled={isApproving === affiliateToApprove?.id || !referralCode.trim()}
                      className="bg-green-600 hover:bg-green-700 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-green-600/25"
                    >
                      {isApproving === affiliateToApprove?.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Approving...
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve & Send Email
                        </>
                      )}
                    </Button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
            style={{
              margin: 0,
              padding: "1rem",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: "spring",
                duration: 0.5,
                bounce: 0.3,
              }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Enhanced Header */}
              <div className="relative bg-gradient-to-r from-red-600 to-red-700 p-6 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-red-600/20 to-transparent"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                      className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                    >
                      <XCircle className="h-6 w-6 text-white" />
                    </motion.div>
                    <div>
                      <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl font-bold text-white"
                      >
                        Reject Affiliate Application
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-white/80 text-sm"
                      >
                        Decline application and notify applicant
                      </motion.p>
                    </div>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        !isRejecting && setShowRejectModal(false)
                      }
                      disabled={isRejecting === affiliateToReject?.id}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-b from-gray-50/50 to-white">
                <div className="space-y-8">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-center"
                  >
                    <p className="text-gray-700 text-base leading-relaxed max-w-md mx-auto">
                      Are you sure you want to reject <strong>{affiliateToReject?.userName}</strong>'s affiliate application? They will be notified via email.
                    </p>
                  </motion.div>

                  {affiliateToReject && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl border border-red-100 p-6"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-10 w-10 bg-red-100 rounded-lg flex items-center justify-center">
                          <AlertTriangle className="h-5 w-5 text-red-600" />
                        </div>
                        <h4 className="text-base font-semibold text-gray-900">
                          Application to be Rejected
                        </h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <span className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <Mail className="h-4 w-4 text-red-500" />
                            Name
                          </span>
                          <p className="text-base font-semibold text-gray-900">
                            {affiliateToReject.userName}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <Mail className="h-4 w-4 text-red-500" />
                            Email
                          </span>
                          <p className="text-base text-gray-900">
                            {affiliateToReject.userEmail}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-red-500" />
                            Application Date
                          </span>
                          <p className="text-base text-gray-900">
                            {formatDate(affiliateToReject.submittedAt)}
                          </p>
                        </div>
                        <div className="space-y-1">
                          <span className="text-sm font-medium text-gray-600 flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-red-500" />
                            Social Profiles
                          </span>
                          <p className="text-base text-gray-900">
                            {affiliateToReject.profiles.length} platform{affiliateToReject.profiles.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-gradient-to-br from-red-50 to-orange-50 border border-red-200 rounded-xl p-4"
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-800">
                          Rejection Consequences
                        </p>
                        <ul className="text-sm text-red-700 mt-1 space-y-1">
                          <li>• Email notification sent immediately</li>
                          <li>• Application status permanently changed</li>
                          <li>• Applicant can reapply in the future</li>
                          <li>• Action is logged for audit purposes</li>
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Enhanced Footer */}
              <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
                <div className="flex items-center justify-end gap-4">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      variant="outline"
                      onClick={() => setShowRejectModal(false)}
                      disabled={isRejecting === affiliateToReject?.id}
                      className="px-6 h-11 rounded-xl font-medium"
                    >
                      Cancel
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={handleRejectConfirm}
                      disabled={isRejecting === affiliateToReject?.id}
                      className="bg-red-600 hover:bg-red-700 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-red-600/25"
                    >
                      {isRejecting === affiliateToReject?.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Rejecting...
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject & Send Email
                        </>
                      )}
                    </Button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Commission Settings Modal */}
      <AnimatePresence>
        {showCommissionModal && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
            style={{
              margin: 0,
              padding: "1rem",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: "spring",
                duration: 0.5,
                bounce: 0.3,
              }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Enhanced Header */}
              <div className="relative bg-gradient-to-r from-jamaica-green to-emerald-600 p-6 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/20 to-transparent"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                      className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                    >
                      <DollarSign className="h-6 w-6 text-white" />
                    </motion.div>
                    <div>
                      <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl font-bold text-white"
                      >
                        Commission Settings
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-white/80 text-sm"
                      >
                        Configure affiliate commission rates
                      </motion.p>
                    </div>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowCommissionModal(false)}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
                <div className="p-6 space-y-6">
                  
                  {/* Current Rate Overview Card */}
                  {commissionSettings && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-10 w-10 bg-gradient-to-br from-jamaica-green to-emerald-600 rounded-lg flex items-center justify-center">
                          <TrendingUp className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Current Commission Rate</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="h-8 w-8 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                            <DollarSign className="h-4 w-4 text-jamaica-green" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Active Rate</p>
                            <p className="text-2xl font-bold text-gray-900">{commissionSettings.commissionRate}%</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="h-8 w-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                            <Clock className="h-4 w-4 text-blue-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Last Updated</p>
                            <p className="font-semibold text-gray-900">{formatDate(commissionSettings.lastUpdatedAt)}</p>
                            <p className="text-xs text-gray-500">by {commissionSettings.lastUpdatedBy}</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Update Rate Card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Settings className="h-5 w-5 text-jamaica-green" />
                        Update Commission Rate
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Set a new commission rate for all affiliate earnings
                      </p>
                    </div>
                    <div className="p-6">
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            New Commission Rate (%)
                          </label>
                          <div className="relative">
                            <Input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={newCommissionRate}
                              onChange={(e) => setNewCommissionRate(e.target.value)}
                              placeholder="Enter commission rate"
                              className="w-full pl-4 pr-12 py-3 text-lg border-gray-300 focus:border-jamaica-green focus:ring-jamaica-green/20"
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                              <span className="text-gray-500 text-lg font-medium">%</span>
                            </div>
                          </div>
                          <div className="mt-2 flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            <p className="text-xs text-gray-600">
                              Enter a percentage between 0 and 100. This rate will apply to all future affiliate earnings and commissions.
                            </p>
                          </div>
                        </div>

                        {/* Rate Preview */}
                        {newCommissionRate && !isNaN(parseFloat(newCommissionRate)) && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 p-4"
                          >
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <Calculator className="h-5 w-5 text-green-600" />
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900">Preview</h4>
                                <p className="text-xs text-gray-600">
                                  On a $100 sale, affiliate earns: <strong className="text-green-700">${(parseFloat(newCommissionRate) || 0).toFixed(2)}</strong>
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </div>
                  </motion.div>

                  {/* Action Buttons */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex gap-3 pt-2"
                  >
                    <Button
                      variant="outline"
                      onClick={() => setShowCommissionModal(false)}
                      className="flex-1 py-3 border-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCommissionSettingsUpdate}
                      disabled={!newCommissionRate || loadingCommissionSettings}
                      className="flex-1 py-3 bg-jamaica-green hover:bg-jamaica-green/90"
                    >
                      {loadingCommissionSettings ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Updating Rate...
                        </>
                      ) : (
                        <>
                          <Settings className="w-4 h-4 mr-2" />
                          Update Commission Rate
                        </>
                      )}
                    </Button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Payout Settings Modal */}
      <AnimatePresence>
        {showPayoutModal && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
            style={{
              margin: 0,
              padding: "1rem",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: "spring",
                duration: 0.5,
                bounce: 0.3,
              }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Enhanced Header */}
              <div className="relative bg-gradient-to-r from-jamaica-green to-emerald-600 p-6 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/20 to-transparent"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                      className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                    >
                      <Calendar className="h-6 w-6 text-white" />
                    </motion.div>
                    <div>
                      <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl font-bold text-white"
                      >
                        Payout Settings
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-white/80 text-sm"
                      >
                        Configure automated payout schedule
                      </motion.p>
                    </div>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPayoutModal(false)}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
                <div className="p-6 space-y-6">
                  
                  {/* Current Schedule Overview Card */}
                  {payoutSchedule && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                      className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="h-10 w-10 bg-gradient-to-br from-jamaica-green to-emerald-600 rounded-lg flex items-center justify-center">
                          <Calendar className="h-5 w-5 text-white" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Current Payout Schedule</h3>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="h-8 w-8 bg-jamaica-green/10 rounded-lg flex items-center justify-center">
                            <RefreshCw className="h-4 w-4 text-jamaica-green" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Frequency</p>
                            <p className="text-lg font-bold text-gray-900 capitalize">{payoutSchedule.frequency}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className="h-8 w-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                            <Clock className="h-4 w-4 text-blue-500" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-600">Next Payout</p>
                            <p className="font-semibold text-gray-900">{formatDate(payoutSchedule.nextPayoutDate)}</p>
                            <p className="text-xs text-gray-500">at {payoutSchedule.time}</p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {/* Update Schedule Card */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                  >
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <Settings className="h-5 w-5 text-jamaica-green" />
                        Update Payout Schedule
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Configure when affiliate payouts are automatically processed
                      </p>
                    </div>
                    <div className="p-6">
                      <div className="space-y-6">
                        
                        {/* Frequency Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Payout Frequency
                          </label>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => setNewPayoutSettings({...newPayoutSettings, frequency: 'monthly'})}
                              className={`p-4 rounded-lg border-2 transition-all ${
                                newPayoutSettings.frequency === 'monthly'
                                  ? 'border-jamaica-green bg-jamaica-green/5 text-jamaica-green'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <span className="font-medium">Monthly</span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Every month on the specified day</p>
                            </button>
                            <button
                              type="button"
                              onClick={() => setNewPayoutSettings({...newPayoutSettings, frequency: 'quarterly'})}
                              className={`p-4 rounded-lg border-2 transition-all ${
                                newPayoutSettings.frequency === 'quarterly'
                                  ? 'border-jamaica-green bg-jamaica-green/5 text-jamaica-green'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <span className="font-medium">Quarterly</span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">Every quarter on the specified day</p>
                            </button>
                          </div>
                        </div>

                        {/* Day of Month */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Day of Month
                          </label>
                          <Input
                            type="number"
                            min="1"
                            max="31"
                            value={newPayoutSettings.dayOfMonth}
                            onChange={(e) => setNewPayoutSettings({...newPayoutSettings, dayOfMonth: parseInt(e.target.value) || 1})}
                            placeholder="Enter day (1-31)"
                            className="w-full border-gray-300 focus:border-jamaica-green focus:ring-jamaica-green/20"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            The day of the month when payouts will be processed
                          </p>
                        </div>

                        {/* Time */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Processing Time
                          </label>
                          <Input
                            type="time"
                            value={newPayoutSettings.time}
                            onChange={(e) => setNewPayoutSettings({...newPayoutSettings, time: e.target.value})}
                            className="w-full border-gray-300 focus:border-jamaica-green focus:ring-jamaica-green/20"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            The time when payouts will be processed (24-hour format)
                          </p>
                        </div>

                        {/* Preview */}
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-100 p-4"
                        >
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                              <Calendar className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <h4 className="text-sm font-semibold text-gray-900">Preview</h4>
                              <p className="text-xs text-gray-600">
                                Next payout: <strong className="text-green-700">
                                  {formatDate(calculateNextPayoutDate(newPayoutSettings.frequency, newPayoutSettings.dayOfMonth))} at {newPayoutSettings.time}
                                </strong>
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      </div>
                    </div>
                  </motion.div>

                  {/* Action Buttons */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex gap-3 pt-2"
                  >
                    <Button
                      variant="outline"
                      onClick={() => setShowPayoutModal(false)}
                      className="flex-1 py-3 border-gray-300 hover:bg-gray-50"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handlePayoutSettingsUpdate}
                      disabled={loadingPayoutSettings}
                      className="flex-1 py-3 bg-jamaica-green hover:bg-jamaica-green/90"
                    >
                      {loadingPayoutSettings ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Updating Schedule...
                        </>
                      ) : (
                        <>
                          <Calendar className="w-4 h-4 mr-2" />
                          Update Payout Schedule
                        </>
                      )}
                    </Button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Payout Preview Modal */}
      <AnimatePresence>
        {showPayoutPreviewModal && selectedAffiliateForPayout && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4"
            style={{
              margin: 0,
              padding: "1rem",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{
                type: "spring",
                duration: 0.5,
                bounce: 0.3,
              }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Enhanced Header */}
              <div className="relative bg-gradient-to-r from-jamaica-green to-emerald-600 p-6 flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-jamaica-green/20 to-transparent"></div>
                <div className="relative flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                      className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                    >
                      <DollarSign className="h-6 w-6 text-white" />
                    </motion.div>
                    <div>
                      <motion.h2
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-xl font-bold text-white"
                      >
                        Payout Preview
                      </motion.h2>
                      <motion.p
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-white/80 text-sm"
                      >
                        {selectedAffiliateForPayout.userName} - Previous Month Commissions
                      </motion.p>
                    </div>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowPayoutPreviewModal(false)}
                      className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </motion.div>
                </div>
              </div>

              {/* Enhanced Content */}
              <div className="flex-1 overflow-y-auto bg-gradient-to-b from-gray-50/50 to-white">
                <div className="p-6 space-y-6">

                  {loadingPayoutPreview ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-jamaica-green" />
                      <span className="ml-3 text-gray-600">Loading payout data...</span>
                    </div>
                  ) : (
                    <>
                      {/* Payout Summary Cards */}
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="grid grid-cols-1 md:grid-cols-3 gap-4"
                      >
                        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-green-800">Total Payout</p>
                                <p className="text-2xl font-bold text-green-900">
                                  {formatCurrency(payoutPreviewData.totalAmount)}
                                </p>
                              </div>
                              <div className="h-12 w-12 bg-green-200 rounded-full flex items-center justify-center">
                                <DollarSign className="h-6 w-6 text-green-600" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-blue-800">Commissions</p>
                                <p className="text-2xl font-bold text-blue-900">
                                  {payoutPreviewData.commissionCount}
                                </p>
                              </div>
                              <div className="h-12 w-12 bg-blue-200 rounded-full flex items-center justify-center">
                                <BarChart3 className="h-6 w-6 text-blue-600" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>

                        <Card className={`${payoutPreviewData.hasExistingPayout ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-200' : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'}`}>
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className={`text-sm font-medium ${payoutPreviewData.hasExistingPayout ? 'text-red-800' : 'text-gray-800'}`}>
                                  Payment Status
                                </p>
                                <p className={`text-lg font-bold ${payoutPreviewData.hasExistingPayout ? 'text-red-900' : 'text-gray-900'}`}>
                                  {payoutPreviewData.hasExistingPayout ? 'Already Paid' : 'Pending'}
                                </p>
                              </div>
                              <div className={`h-12 w-12 ${payoutPreviewData.hasExistingPayout ? 'bg-red-200' : 'bg-gray-200'} rounded-full flex items-center justify-center`}>
                                {payoutPreviewData.hasExistingPayout ? (
                                  <AlertTriangle className="h-6 w-6 text-red-600" />
                                ) : (
                                  <Clock className="h-6 w-6 text-gray-600" />
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>

                      {/* Commission Records Table */}
                      {previousMonthCommissions.length > 0 ? (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                          className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-300"
                        >
                          <div className="p-4 bg-gray-50 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                              <DollarSign className="h-5 w-5 text-jamaica-green" />
                              Previous Month Commissions ({previousMonthCommissions.length})
                            </h3>
                            <p className="text-sm text-gray-600 mt-1">
                              All commissions from the previous month are shown below
                            </p>
                          </div>
                          <div className="overflow-x-auto">
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-gray-50">
                                  <TableHead className="font-semibold">Order ID</TableHead>
                                  <TableHead className="font-semibold">Date</TableHead>
                                  <TableHead className="font-semibold">Order Value</TableHead>
                                  <TableHead className="font-semibold">Commission</TableHead>
                                  <TableHead className="font-semibold">Source</TableHead>
                                  <TableHead className="font-semibold">Status</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {previousMonthCommissions.map((commission) => (
                                  <TableRow key={commission.id}>
                                    <TableCell className="font-mono text-sm">
                                      {commission.orderId.slice(-8)}
                                    </TableCell>
                                    <TableCell>
                                      {formatDate(new Date(commission.createdAt))}
                                    </TableCell>
                                    <TableCell>
                                      {formatCurrency(commission.orderValue)}
                                    </TableCell>
                                    <TableCell className="font-semibold text-jamaica-green">
                                      {formatCurrency(commission.commissionAmount)}
                                    </TableCell>
                                    <TableCell>
                                      <div className="flex items-center gap-2">
                                        {commission.source === 'discount_code' ? (
                                          <>
                                            <Tag className="w-3 h-3 text-purple-500" />
                                            <span className="text-xs font-medium text-purple-700">
                                              Discount
                                            </span>
                                          </>
                                        ) : (
                                          <>
                                            <ExternalLink className="w-3 h-3 text-blue-500" />
                                            <span className="text-xs font-medium text-blue-700">
                                              Link
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell>
                                      <Badge variant={commission.status === 'pending' ? 'secondary' : 'default'}>
                                        {commission.status}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                          className="bg-white rounded-xl border border-gray-200 shadow-sm"
                        >
                          <div className="p-4 bg-gray-50 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                              <AlertCircle className="h-5 w-5 text-gray-500" />
                              Commission Records
                            </h3>
                          </div>
                          <div className="text-center py-12">
                            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Commissions Found</h3>
                            <p className="text-gray-600">
                              This affiliate has no commissions from the previous month.
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Enhanced Footer */}
              <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
                <div className="flex items-center justify-end gap-4">
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      variant="outline"
                      onClick={() => setShowPayoutPreviewModal(false)}
                      className="px-6 h-11 rounded-xl font-medium"
                    >
                      Cancel
                    </Button>
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Button
                      onClick={handlePayoutConfirm}
                      disabled={payoutPreviewData.totalAmount <= 0 || payoutPreviewData.hasExistingPayout || loadingPayouts}
                      className="px-8 h-11 rounded-xl font-semibold bg-jamaica-green hover:bg-jamaica-green/90 shadow-lg shadow-jamaica-green/25"
                    >
                      {payoutPreviewData.hasExistingPayout ? (
                        'Already Processed'
                      ) : payoutPreviewData.totalAmount <= 0 ? (
                        'No Amount to Pay'
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4 mr-2" />
                          Process Payout ({formatCurrency(payoutPreviewData.totalAmount)})
                        </>
                      )}
                    </Button>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Payout Confirmation Modal */}
      <ConfirmDialog
        isOpen={showPayoutConfirmation}
        onClose={() => setShowPayoutConfirmation(false)}
        onConfirm={executePayoutConfirm}
        loading={loadingPayouts}
        title="Confirm Payout"
        description={
          selectedAffiliateForPayout 
            ? `Are you sure you want to process a payout of ${formatCurrency(payoutPreviewData.totalAmount)} to ${selectedAffiliateForPayout.userName}? This action cannot be undone.`
            : 'Confirm payout processing'
        }
        confirmText="Process Payout"
        variant="default"
        type="default"
      />

      {/* Commission Settings Confirmation Modal */}
      <ConfirmDialog
        isOpen={showCommissionConfirmModal}
        onClose={() => setShowCommissionConfirmModal(false)}
        onConfirm={handleCommissionConfirmationUpdate}
        loading={loadingCommissionSettings}
        title="Update Commission Rate"
        description={`Are you sure you want to update the commission rate to ${newCommissionRate}%? This will affect all future affiliate earnings and commissions.`}
        confirmText="Update Rate"
        variant="default"
        type="default"
      />

      {/* Payout Settings Confirmation Modal */}
      <ConfirmDialog
        isOpen={showPayoutConfirmModal}
        onClose={() => setShowPayoutConfirmModal(false)}
        onConfirm={handlePayoutConfirmationUpdate}
        loading={loadingPayoutSettings}
        title="Update Payout Schedule"
        description={`Are you sure you want to update the payout schedule to ${newPayoutSettings.frequency} on day ${newPayoutSettings.dayOfMonth} at ${newPayoutSettings.time}? This will affect all future automated payouts.`}
        confirmText="Update Schedule"
        variant="default"
        type="default"
      />
    </div>
  );
};

export default Affiliates;